import {
  useForm,
  FieldValues,
  UseFormSetValue,
  FormProvider,
} from "react-hook-form";
import { useEffect } from "react";
import { FaRegCircleCheck } from "react-icons/fa6";
import { FiRefreshCw } from "react-icons/fi";
import DynamicInput from "./DynamicInput";
import Button from "components/button/Button";
import { Modal } from "components/modal";
import { useTranslation } from "react-i18next";
import { formatDateTime, trimStrings } from "services/helper";

export type Option = { label: string; value: string | number; key?: string };
export type FieldChangeType = (
  value: string | number | null,
  setValue?: UseFormSetValue<FieldValues>
) => void;

export type Field = {
  name: string;
  label: string;
  type: string;
  formed?: boolean;
  options?: Option[];
  required?: boolean;
  multiple?: boolean;
  disabled?: boolean;
  rows?: number;
  onSearch?: (text: string) => Promise<Option[]>;
  onRetry?: () => void;
  onFieldChange?: FieldChangeType;
  onBeforeChange?: (value: any, formValues: any) => Promise<boolean>;
  filtered?: boolean;
  showWhen?: {
    field: string;
    equals?: any;
    optionKey?: string;
    optionValue?: any;
  };
  permissions?: string[];
  picker?: string;
  hidden?: boolean;
  accept?: string;
  unitOptions?: Option[];
  unitMetaField?: string;
  gridCol?: number;
  defaultSelectedOptions?: Option[];
  arrayFields?: Field[];
  defaultValue?: { default: Option };
  other?: boolean;
};

type DynamicFormProps = {
  open: boolean;
  onClose: () => void;
  onSubmit: (formData: FieldValues) => void | Promise<void>;
  fields: Field[];
  defaultValues?: FieldValues;
  title: string;
  description?: string;
  isEdit?: boolean;
  maxWidth?: string;
  loading?: boolean;
};

export default function DynamicForm({
  open,
  onClose,
  onSubmit,
  fields,
  defaultValues = {},
  title,
  description,
  isEdit,
  maxWidth = "md:max-w-[600px] lg:max-w-[750px]",
  loading,
}: DynamicFormProps) {
  const { t } = useTranslation();

  const methods = useForm({ defaultValues });
  const {
    handleSubmit,
    control,
    reset,
    setValue,
    formState: { dirtyFields },
  } = methods;

  useEffect(() => {
    const fixedDefaults = { ...defaultValues };
    fields.forEach((field) => {
      const val = fixedDefaults[field.name];
      if (val === undefined || val === null) {
        if (field.type === "date" || field.type === "time")
          fixedDefaults[field.name] = null;
        else if (field.type === "select" && field.multiple)
          fixedDefaults[field.name] = [];
        else fixedDefaults[field.name] = "";
      } else {
        if (
          (field.type === "date" || field.type === "time") &&
          typeof val === "string"
        ) {
          const d = new Date(val);
          fixedDefaults[field.name] = isNaN(d.getTime()) ? null : d;
        }
      }
    });

    reset(fixedDefaults);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  type FormResult = Record<string, unknown>;

  const submitHandler = (data: FieldValues) => {
    const trimmedData = trimStrings(data);
    const formattedData = formatDateTime(trimmedData, fields);

    const result: FormResult = isEdit
      ? (Object.keys(dirtyFields) as Array<keyof typeof formattedData>).reduce(
          (acc, key) => {
            acc[key] = formattedData[key];
            return acc;
          },
          {} as FormResult
        )
      : formattedData;

    onSubmit(result);
  };

  return (
    <Modal
      isOpen={open}
      onClose={onClose}
      className={`max-w-[750px] p-5 lg:p-7 ${maxWidth}`}
    >
      <FormProvider {...methods}>
        <form onSubmit={handleSubmit(submitHandler)} noValidate>
          <h4 className="mb-6 text-lg font-medium text-gray-800 dark:text-white/90">
            {title}
          </h4>
          {description && (
            <p className="text-sm text-gray-500 dark:text-gray-400">
              {description}
            </p>
          )}
          <div className="mb-7 mt-2 border" />

          <div
            className={`custom-scrollbar grid max-h-[65vh] grid-cols-1 gap-x-6 gap-y-5 overflow-y-auto p-2 md:grid-cols-2`}
          >
            <DynamicInput
              fields={fields}
              control={control}
              setValue={setValue}
            />
          </div>

          <div className="mt-6 flex w-full items-center justify-between gap-3">
            <Button
              variant="outline"
              size="sm"
              onClick={() => reset()}
              startIcon={<FiRefreshCw size={16} />}
            >
              {t("buttons.reset")}
            </Button>

            <div className="flex items-center gap-2">
              <Button variant="error" size="sm" onClick={onClose}>
                {t("buttons.cancel")}
              </Button>
              <Button
                type="submit"
                variant="primary"
                size="sm"
                loading={loading}
                startIcon={<FaRegCircleCheck size={16} />}
              >
                {t("buttons.submit")}
              </Button>
            </div>
          </div>
        </form>
      </FormProvider>
    </Modal>
  );
}
